import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import moment from "moment";
import React from "react";

import { classNames } from "../../../helpers";
import axiosInstance from "../../../utils/axiosInstance";
import { getUrlForBookings } from "../../../utils/confighelpers";
import { Icon } from "../../classes/schedule/ui";

/** Shared bits for the Home dashboard and its Members / Bookings / Revenue insights. */

export const COLORS = { confirmed: "#7e00dd", cancelled: "#F64E60", members: "#7e00dd", revenue: "#481B3A" };

const opt = (value, title, size, side, interval, unit) => ({ value, title, size, side, interval, unit });
export const BOOKING_RANGES = [
  { label: "Days", options: [opt("l7d", "Last 7 days", 7, "last", "daily", "days"), opt("l15d", "Last 15 days", 15, "last", "daily", "days"), opt("l30d", "Last 30 days", 30, "last", "daily", "days"), opt("n7d", "Next 7 days", 7, "next", "daily", "days"), opt("n15d", "Next 15 days", 15, "next", "daily", "days"), opt("n30d", "Next 30 days", 30, "next", "daily", "days")] },
  { label: "Weeks", options: [opt("l4w", "Last 4 weeks", 4, "last", "weekly", "weeks"), opt("l12w", "Last 12 weeks", 12, "last", "weekly", "weeks"), opt("l24w", "Last 24 weeks", 24, "last", "weekly", "weeks"), opt("n4w", "Next 4 weeks", 4, "next", "weekly", "weeks"), opt("n12w", "Next 12 weeks", 12, "next", "weekly", "weeks"), opt("n24w", "Next 24 weeks", 24, "next", "weekly", "weeks")] },
  { label: "Months", options: [opt("l3m", "Last 3 months", 3, "last", "monthly", "months"), opt("l6m", "Last 6 months", 6, "last", "monthly", "months"), opt("l12m", "Last 12 months", 12, "last", "monthly", "months"), opt("n3m", "Next 3 months", 3, "next", "monthly", "months"), opt("n6m", "Next 6 months", 6, "next", "monthly", "months"), opt("n12m", "Next 12 months", 12, "next", "monthly", "months")] },
];
export const PAST_RANGES = BOOKING_RANGES.map((g) => ({ ...g, options: g.options.filter((o) => o.side === "last") }));
export const findRange = (groups, v) => groups.flatMap((g) => g.options).find((o) => o.value === v);

export const euro = (n) => "€" + (Math.round((parseFloat(n) || 0) * 100) / 100).toLocaleString("nl-NL", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
export const euroAxis = (n) => (n >= 1000 ? `€${Math.round(n / 100) / 10}k` : `€${Math.round(n)}`);

/** Bucket label: short for the axis, full for the tooltip. */
export const bucketLabels = (b, interval, size) => {
  const s = moment(b.start);
  const e = moment(b.end).subtract(1, "day");
  if (interval === "daily") return { short: size <= 7 ? s.format("ddd D") : s.format("D MMM"), full: s.format("ddd D MMM YYYY") };
  if (interval === "weekly") return { short: s.format("D MMM"), full: `${s.format("D MMM")} – ${e.format("D MMM YYYY")}` };
  return { short: s.format("MMM"), full: s.format("MMMM YYYY") };
};

/**
 * GET bookings/<endpoint> buckets.
 * allBookingStats counts FORWARD from startDate; allMembersStats / allRevenueStats count BACK to (and including) startDate's period.
 */
export const fetchBuckets = async (endpoint, { interval, size, startDate, extra = "" }) => {
  const res = await axiosInstance({
    method: "get",
    url: `${getUrlForBookings()}${endpoint}?periodType=${interval}&page=0&size=${size}&startDate=${moment(startDate).format("YYYY-MM-DD")}${extra}`,
  });
  if (res?.status === 200 && res?.data?.statusCode === 200 && Array.isArray(res.data.data?.buckets)) return res.data.data.buckets;
  throw new Error(typeof res?.data?.message === "string" ? res.data.message : "Couldn't load these stats.");
};

/** startDate for a picker range. */
export const startDateFor = (r, countsForward) => {
  if (!countsForward || r.side === "next") return moment();
  return moment().subtract(r.size - 1, r.unit);
};

export const sumBuckets = (b) => (b || []).reduce((a, x) => a + (parseFloat(x.count) || 0), 0);

/** Compact picker button (label + chevron right next to it) with a grouped menu. */
export function Picker({ label, value, groups, options, onChange, align = "end" }) {
  const flat = groups ? groups.flatMap((g) => g.options.map((o) => ({ value: o.value, label: o.title }))) : options;
  const current = flat.find((o) => o.value === value);
  const renderItem = (o) => (
    <MenuItem key={o.value}>
      <button
        type="button"
        onClick={() => onChange(o.value)}
        className="flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-left text-sm font-bold text-gray-900 data-[focus]:bg-[#F7F4F8]"
      >
        <span className="truncate">{o.label}</span>
        {o.value === value && <Icon className="h-4 w-4 shrink-0 text-indigo-bz" d={<path d="m5 12 5 5L20 7" />} />}
      </button>
    </MenuItem>
  );
  return (
    <Menu as="div" className="relative">
      <MenuButton
        aria-label={label}
        className="inline-flex h-9 max-w-[15rem] items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 text-sm font-extrabold text-gray-900 hover:border-gray-300 data-[open]:border-indigo-bz data-[open]:ring-4 data-[open]:ring-[#F3E9FF]"
      >
        <span className="truncate">{current?.label || label}</span>
        <Icon className="h-3.5 w-3.5 shrink-0 text-gray-500" d={<path d="m6 9 6 6 6-6" />} />
      </MenuButton>
      <MenuItems anchor={`bottom ${align}`} className="z-[60] mt-1.5 max-h-80 w-56 overflow-y-auto rounded-2xl border border-gray-200 bg-white p-1.5 shadow-xl focus:outline-none">
        {groups
          ? groups.map((g) => (
              <div key={g.label}>
                <p className="px-3 pb-1 pt-2 text-[11px] font-extrabold uppercase tracking-wide text-gray-400">{g.label}</p>
                {g.options.map((o) => renderItem({ value: o.value, label: o.title }))}
              </div>
            ))
          : flat.map(renderItem)}
      </MenuItems>
    </Menu>
  );
}

/** White card with a title row (title, optional subtitle, right-side action). */
export function Panel({ title, subtitle, action, children, className = "", bodyClassName = "" }) {
  return (
    <section className={classNames("rounded-[20px] border border-gray-200 bg-white p-4 shadow-[0_1px_2px_rgba(29,19,32,0.04)] sm:p-5", className)}>
      {(title || action) && (
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            {title && <h2 className="text-base font-black text-gray-900">{title}</h2>}
            {subtitle && <div className="mt-0.5 text-[13px] text-gray-500">{subtitle}</div>}
          </div>
          {action}
        </div>
      )}
      <div className={bodyClassName}>{children}</div>
    </section>
  );
}

export function LinkButton({ onClick, children }) {
  return (
    <button type="button" onClick={onClick} className="rounded-lg px-2 py-1.5 text-[13px] font-extrabold text-indigo-bz hover:bg-[#F3E9FF]">
      {children}
    </button>
  );
}

export function ListSkeleton({ rows = 4 }) {
  return (
    <div aria-hidden="true">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex animate-pulse items-center gap-3 border-t border-gray-100 py-3 first:border-t-0">
          <span className="h-8 w-8 rounded-full bg-[#EDE5F0]" />
          <span className="flex-1">
            <span className="block h-3.5 w-40 max-w-full rounded-full bg-[#EDE5F0]" />
            <span className="mt-2 block h-3 w-56 max-w-full rounded-full bg-[#F3EEF5]" />
          </span>
        </div>
      ))}
    </div>
  );
}

export function EmptyLine({ children }) {
  return <p className="rounded-2xl bg-[#FAF8FB] px-4 py-6 text-center text-sm font-semibold text-gray-500">{children}</p>;
}

export const axisTick = { fill: "#7b737d", fontSize: 11, fontWeight: 700 };

export function TooltipBox({ active, payload, rows }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="min-w-[150px] rounded-xl bg-[#1d1320] px-3 py-2.5 text-xs font-semibold text-white shadow-xl">
      <p className="mb-1 font-black">{d.full}</p>
      {rows(d).map((r) => (
        <p key={r.label} className="flex items-center gap-2">
          {r.color && <span className="h-2.5 w-2.5 rounded-[3px]" style={{ background: r.color }} />}
          {r.label}
          <b className="ml-auto pl-3">{r.value}</b>
        </p>
      ))}
    </div>
  );
}

export function ChartShell({ loading, error, onRetry, empty, emptyText, height = 260, children }) {
  return (
    <div className="relative mt-4" style={{ height }}>
      {children}
      {loading && (
        <div className="absolute inset-0 flex items-end gap-2 rounded-2xl bg-white px-8 pb-8 pt-4" aria-hidden="true">
          {[40, 65, 50, 80, 35, 70, 55].map((h, i) => (
            <span key={i} className="flex-1 animate-pulse rounded-t-md bg-[#F3EEF5]" style={{ height: `${h}%` }} />
          ))}
        </div>
      )}
      {!loading && error && (
        <div className="absolute inset-0 flex flex-col items-center justify-center rounded-2xl bg-white text-center">
          <p className="text-sm font-bold text-gray-700">{error}</p>
          <button type="button" onClick={onRetry} className="mt-2 rounded-xl px-4 py-2 text-sm font-extrabold text-indigo-bz hover:bg-[#F3E9FF]">
            Try again
          </button>
        </div>
      )}
      {!loading && !error && empty && (
        <div className="pointer-events-none absolute inset-x-0 top-1/3 flex justify-center">
          <span className="rounded-full bg-white/95 px-4 py-2 text-sm font-bold text-gray-500 shadow-sm">{emptyText}</span>
        </div>
      )}
    </div>
  );
}

/** Original Home icons (members: heroicons UserGroup solid; bookings + revenue: the menu_*_solid svgs). */
export const KPI_ICONS = {
  members: (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5" aria-hidden="true">
      <path d="M10 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM6 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM1.49 15.326a.78.78 0 0 1-.358-.442 3 3 0 0 1 4.308-3.516 6.484 6.484 0 0 0-1.905 3.959c-.023.222-.014.442.025.654a4.97 4.97 0 0 1-2.07-.655ZM16.44 15.98a4.97 4.97 0 0 0 2.07-.654.78.78 0 0 0 .357-.442 3 3 0 0 0-4.308-3.517 6.484 6.484 0 0 1 1.907 3.96 2.32 2.32 0 0 1-.026.654ZM18 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM5.304 16.19a.844.844 0 0 1-.277-.71 5 5 0 0 1 9.947 0 .843.843 0 0 1-.277.71A6.975 6.975 0 0 1 10 18a6.974 6.974 0 0 1-4.696-1.81Z" />
    </svg>
  ),
  bookings: (
    <svg viewBox="0 0 18 20" fill="currentColor" className="h-[19px] w-[17px]" aria-hidden="true">
      <path fillRule="evenodd" clipRule="evenodd" d="M13.4109 0.768617L13.4119 1.51824C16.1665 1.73413 17.9862 3.61119 17.9891 6.48975L18 14.9155C18.0039 18.054 16.0322 19.985 12.8718 19.99L5.15188 20C2.01119 20.004 0.0148166 18.027 0.0108673 14.8795L6.64975e-06 6.55272C-0.00394266 3.65517 1.75153 1.78311 4.50617 1.53024L4.50518 0.780611C4.5042 0.340832 4.83001 0.00999726 5.26444 0.00999726C5.69886 0.00899776 6.02468 0.338833 6.02567 0.778612L6.02666 1.47826L11.8914 1.47027L11.8904 0.770616C11.8894 0.330837 12.2152 0.00100177 12.6497 2.26549e-06C13.0742 -0.000997234 13.4099 0.328838 13.4109 0.768617ZM1.52148 6.86157L16.4696 6.84158V6.49175C16.4272 4.34283 15.349 3.21539 13.4138 3.04748L13.4148 3.81709C13.4148 4.24688 13.0801 4.5877 12.6556 4.5877C12.2212 4.5887 11.8943 4.24887 11.8943 3.81909L11.8934 3.0095L6.02863 3.01749L6.02962 3.82609C6.02962 4.25687 5.70479 4.5967 5.27036 4.5967C4.83594 4.5977 4.50913 4.25887 4.50913 3.82809L4.50815 3.05847C2.58286 3.25137 1.51753 4.38281 1.52049 6.55072L1.52148 6.86157ZM12.2399 11.4043V11.4153C12.2498 11.8751 12.625 12.2239 13.0801 12.2139C13.5244 12.2029 13.8789 11.8221 13.869 11.3623C13.8483 10.9225 13.4918 10.5637 13.0485 10.5647C12.5944 10.5747 12.2389 10.9445 12.2399 11.4043ZM13.0554 15.892C12.6013 15.882 12.235 15.5032 12.234 15.0435C12.2241 14.5837 12.5884 14.2029 13.0426 14.1919H13.0525C13.5165 14.1919 13.8927 14.5707 13.8927 15.0405C13.8937 15.5102 13.5185 15.891 13.0554 15.892ZM8.17212 11.4203C8.19187 11.8801 8.56804 12.2389 9.02221 12.2189C9.46651 12.1979 9.82096 11.8181 9.80121 11.3583C9.79035 10.9085 9.42504 10.5587 8.98074 10.5597C8.52657 10.5797 8.17113 10.9605 8.17212 11.4203ZM9.02616 15.8471C8.57199 15.8671 8.1968 15.5082 8.17607 15.0485C8.17607 14.5887 8.53052 14.2089 8.98469 14.1879C9.42899 14.1869 9.79529 14.5367 9.80516 14.9855C9.82589 15.4463 9.47046 15.8261 9.02616 15.8471ZM4.10433 11.4553C4.12408 11.915 4.50025 12.2749 4.95442 12.2539C5.39872 12.2339 5.75317 11.8531 5.73243 11.3933C5.72256 10.9435 5.35725 10.5937 4.91196 10.5947C4.45779 10.6147 4.10334 10.9955 4.10433 11.4553ZM4.95837 15.8521C4.5042 15.8731 4.12901 15.5132 4.10828 15.0535C4.10729 14.5937 4.46273 14.2129 4.9169 14.1929C5.3612 14.1919 5.7275 14.5417 5.73737 14.9915C5.7581 15.4513 5.40365 15.8321 4.95837 15.8521Z" />
    </svg>
  ),
  revenue: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5" aria-hidden="true">
      <path fillRule="evenodd" clipRule="evenodd" d="M17.7689 8.3818H22C22 4.98459 19.9644 3 16.5156 3H7.48444C4.03556 3 2 4.98459 2 8.33847V15.6615C2 19.0154 4.03556 21 7.48444 21H16.5156C19.9644 21 22 19.0154 22 15.6615V15.3495H17.7689C15.8052 15.3495 14.2133 13.7975 14.2133 11.883C14.2133 9.96849 15.8052 8.41647 17.7689 8.41647V8.3818ZM17.7689 9.87241H21.2533C21.6657 9.87241 22 10.1983 22 10.6004V13.131C21.9952 13.5311 21.6637 13.8543 21.2533 13.8589H17.8489C16.8548 13.872 15.9855 13.2084 15.76 12.2643C15.6471 11.6783 15.8056 11.0736 16.1931 10.6122C16.5805 10.1509 17.1573 9.88007 17.7689 9.87241ZM17.92 12.533H18.2489C18.6711 12.533 19.0133 12.1993 19.0133 11.7877C19.0133 11.3761 18.6711 11.0424 18.2489 11.0424H17.92C17.7181 11.0401 17.5236 11.1166 17.38 11.255C17.2364 11.3934 17.1555 11.5821 17.1556 11.779C17.1555 12.1921 17.4964 12.5282 17.92 12.533ZM6.73778 8.3818H12.3822C12.8044 8.3818 13.1467 8.04812 13.1467 7.63649C13.1467 7.22487 12.8044 6.89119 12.3822 6.89119H6.73778C6.31903 6.89116 5.9782 7.2196 5.97333 7.62783C5.97331 8.04087 6.31415 8.37705 6.73778 8.3818Z" />
    </svg>
  ),
};

/* ---------- what "Revenue" covers ---------- */

/** Date the club's FitClub account was created (users.createdAt), e.g. "12 March 2024"; "" when unknown. */
export const clubSinceDate = (userData) => {
  const d = userData?.createdAt ? moment(userData.createdAt) : null;
  return d && d.isValid() ? d.format("D MMMM YYYY") : "";
};

export const revenueScopeText = (since) =>
  `Revenue counts payments made through the FitClub platform for your club${since ? ` since ${since}` : ""}: credit passes, subscriptions and classes paid online. Payments outside the platform, such as cash, bank transfers or other systems, are not included.`;

/** Small "i" icon that shows a note on hover or keyboard focus. */
export function InfoTip({ text, label = "More info", light = false, dark = false }) {
  return (
    <span className="group relative inline-flex">
      <button
        type="button"
        aria-label={label}
        onClick={(e) => e.stopPropagation()}
        className={classNames(
          "flex h-6 w-6 items-center justify-center rounded-full focus:outline-none focus-visible:ring-2",
          light ? "text-white/80 hover:bg-white/15 hover:text-white focus-visible:ring-white" : dark ? "text-[#3A1530]/75 hover:bg-white/30 hover:text-[#3A1530] focus-visible:ring-[#3A1530]" : "text-gray-400 hover:bg-[#F7F4F8] hover:text-gray-700 focus:text-gray-700 focus-visible:ring-indigo-bz"
        )}
      >
        <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4" aria-hidden="true">
          <path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM9 9a.75.75 0 0 0 0 1.5h.253a.25.25 0 0 1 .244.304l-.459 2.066A1.75 1.75 0 0 0 10.747 15H11a.75.75 0 0 0 0-1.5h-.253a.25.25 0 0 1-.244-.304l.459-2.066A1.75 1.75 0 0 0 9.253 9H9Z" clipRule="evenodd" />
        </svg>
      </button>
      <span
        role="tooltip"
        className={classNames(
          "pointer-events-none invisible absolute left-1/2 top-full z-40 mt-1.5 w-64 -translate-x-1/2 rounded-xl bg-gray-900 px-3.5 py-2.5 text-left text-xs font-semibold leading-relaxed text-white opacity-0 shadow-lg transition group-focus-within:visible group-focus-within:opacity-100 group-hover:visible group-hover:opacity-100",
        )}
      >
        {text}
      </span>
    </span>
  );
}
